import React, { useEffect, useRef, useState } from 'react';
import nipplejs from 'nipplejs';
import { useMobileDetection } from '../../../../hooks/useMobileDetection';

const MobileJoystick = ({ movementSystem, className }) => {
  const { isMobile } = useMobileDetection();
  const joystickRef = useRef(null);
  const managerRef = useRef(null);
  const [isActive, setIsActive] = useState(false);

  console.log('MobileJoystick render:', { 
    isMobile, 
    movementSystem: !!movementSystem,
    isViewMode: movementSystem?.isViewMode,
    isLocked: movementSystem?.isLocked,
    isEnabled: movementSystem?.isEnabled
  });

  useEffect(() => {
    if (!isMobile || !movementSystem || !joystickRef.current) return;

    // Automatically enable view mode for mobile users so they can move around
    if (!movementSystem.isViewMode && !movementSystem.isLocked) {
      console.log('Auto-enabling view mode for mobile user');
      movementSystem.setViewMode(true);
    }

    // Create joystick manager
    const manager = nipplejs.create({
      zone: joystickRef.current,
      mode: 'static',
      position: { left: '50%', top: '50%' },
      color: 'rgba(255, 255, 255, 0.8)',
      size: 120,
      threshold: 0.1,
      fadeTime: 250,
      multitouch: false,
      maxNumberOfNipples: 1,
      dataOnly: false,
      restJoystick: true,
      restOpacity: 0.5,
      lockX: false,
      lockY: false
    });

    managerRef.current = manager;

    // Handle joystick movement
    manager.on('move', (evt, data) => {
      setIsActive(true);
      
      if (!data.vector) return;

      // Get normalized movement values (-1 to 1)
      const x = data.vector.x;
      const y = data.vector.y; // Use Y as-is (up on joystick = forward)

      // Apply deadzone
      const deadzone = 0.15;
      const adjustedX = Math.abs(x) > deadzone ? x : 0;
      const adjustedY = Math.abs(y) > deadzone ? y : 0;

      console.log('Joystick movement:', { x: adjustedX, y: adjustedY, movementSystem: !!movementSystem });

      // Update movement system
      if (movementSystem && movementSystem.setMovementVector) {
        movementSystem.setMovementVector(adjustedX, adjustedY);
      } else if (movementSystem) {
        // Fallback for existing movement system
        movementSystem.moveLeft = adjustedX < -0.3;
        movementSystem.moveRight = adjustedX > 0.3;
        movementSystem.moveForward = adjustedY > 0.3;
        movementSystem.moveBackward = adjustedY < -0.3;
        console.log('Movement states:', {
          left: movementSystem.moveLeft,
          right: movementSystem.moveRight,
          forward: movementSystem.moveForward,
          backward: movementSystem.moveBackward
        });
      } else {
        console.warn('MovementSystem not available');
      }
    });

    // Handle joystick release
    manager.on('end', () => {
      setIsActive(false);
      
      console.log('Joystick released');
      
      // Stop all movement
      if (movementSystem && movementSystem.setMovementVector) {
        movementSystem.setMovementVector(0, 0);
      } else if (movementSystem) {
        // Fallback for existing movement system
        movementSystem.moveLeft = false;
        movementSystem.moveRight = false;
        movementSystem.moveForward = false;
        movementSystem.moveBackward = false;
      }
    });

    // Cleanup
    return () => {
      if (managerRef.current) {
        managerRef.current.destroy();
        managerRef.current = null;
      }
    };
  }, [isMobile, movementSystem]);

  // Don't render on desktop
  if (!isMobile) return null;

  return (
    <div 
      ref={joystickRef}
      className={`mobile-joystick ${className || ''} ${isActive ? 'active' : ''}`}
      style={{
        position: 'fixed',
        bottom: '140px', // Move well above mobile UI bars
        left: '20px',
        width: '120px',
        height: '120px',
        zIndex: 1001, // Higher z-index
        borderRadius: '50%',
        background: 'rgba(0, 255, 163, 0.2)', // Distinctive green background to verify it's updated
        border: '3px solid rgba(0, 255, 163, 0.5)',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.2s ease',
        opacity: isActive ? 1 : 0.8,
        transform: isActive ? 'scale(1.05)' : 'scale(1)',
        pointerEvents: 'all',
        boxShadow: '0 4px 20px rgba(0, 255, 163, 0.3)' // Glow effect to make it more visible
      }}
    />
  );
};

export default MobileJoystick;